import React, { useEffect, useState, useRef } from 'react';
import { formatTime } from '@/lib/formatTime';
import { CaloriesBurned } from './CaloriesBurned';
import { Clock } from 'lucide-react';
import { format } from 'date-fns';

interface TimerDisplayProps {
  timeElapsed: number;
  timeRemaining: number;
  isActive: boolean;
  totalDuration: number;
}

export const TimerDisplay: React.FC<TimerDisplayProps> = ({
  timeElapsed,
  timeRemaining,
  isActive,
  totalDuration
}) => {
  const [displayTime, setDisplayTime] = useState(totalDuration);
  const [estimatedEndTime, setEstimatedEndTime] = useState<Date | null>(null);
  const prevTimeRef = useRef<number>(timeRemaining);
  
  // Update the display time when active state or times change
  useEffect(() => {
    // Only update if the change is significant or status changed
    if (Math.abs(prevTimeRef.current - timeRemaining) >= 0.5 || prevTimeRef.current !== timeRemaining) {
      setDisplayTime(isActive ? timeRemaining : totalDuration);
      prevTimeRef.current = timeRemaining;
    }
  }, [isActive, timeRemaining, totalDuration]);
  
  // Calculate the estimated end time when the timer starts or changes
  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      const now = new Date();
      const endTimeMs = now.getTime() + (timeRemaining * 1000);
      setEstimatedEndTime(new Date(endTimeMs));
    } else if (!isActive) {
      setEstimatedEndTime(null);
    }
  }, [isActive, timeRemaining]);

  return (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
      <div className={`text-4xl font-bold text-fasttrack-blue ${isActive ? 'timer-pulse' : ''}`}>
        {formatTime(displayTime)}
      </div>
      <div className="text-sm text-muted-foreground mt-2">
        {isActive ? "Remaining" : "Duration"}
      </div>
      
      {isActive && <CaloriesBurned />}
      
      {estimatedEndTime && (
        <div className="mt-3 flex items-center justify-center text-xs text-muted-foreground">
          <Clock size={12} className="mr-1 text-fasttrack-blue" />
          <span>Ends at: <span className="font-medium text-fasttrack-blue">
            {format(estimatedEndTime, "h:mm a, MMM d")}
          </span></span>
        </div>
      )}
    </div>
  );
};
